/**
 * Utility for generating unique identifiers with multiple fallback strategies
 */

/**
 * Generates a UUID v4 using the best available method
 * Falls back to alternative implementations if crypto.randomUUID is unavailable
 */
export function generateUUID(): string {
  // Strategy 1: Use crypto.randomUUID if available (most secure, fastest)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch (error) {
      console.warn('crypto.randomUUID failed, falling back to alternative method:', error);
    }
  }

  // Strategy 2: Use crypto.getRandomValues if available (secure, widely supported)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    try {
      const array = new Uint8Array(16);
      crypto.getRandomValues(array);

      // Set version (4) and variant bits
      array[6] = (array[6] & 0x0f) | 0x40;
      array[8] = (array[8] & 0x3f) | 0x80;

      // Convert to hex string
      const hex = Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');

      // Format as UUID
      return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20, 32)].join('-');
    } catch (error) {
      console.warn('crypto.getRandomValues failed, falling back to Math.random:', error);
    }
  }

  // Strategy 3: Use Math.random (less secure but always available)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
